import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowRight,
  Download,
  Edit,
  History,
  Loader2,
  Plus,
  RefreshCw,
  Repeat,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";

import { API, dataCache } from "./utils";


function PolesTab({ onUserClick }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [poles, setPoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [lengthFilter, setLengthFilter] = useState('all');
  const [weightFilter, setWeightFilter] = useState('all');
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'available'
  
  // Create/Edit dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPole, setEditingPole] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Pole history dialog state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyPole, setHistoryPole] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    pole_code: '',
    serial_num: '',
    length: '',
    weight: '',
    brand: 'ESSX',
    status: 'in stock',
    tag_id: ''
  });

  const fetchPoles = async (forceRefresh = false) => {
    const cacheKey = `poles_${viewMode}`;
    
    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = dataCache.get(cacheKey);
      if (cached) {
        setPoles(cached);
        setLoading(false);
        return;
      }
    }
    
    setLoading(true);
    try {
      // Fetch based on view mode
      const endpoint = viewMode === 'available' ? `${API}/poles/available` : `${API}/poles/all`;
      const response = await axios.get(endpoint);
      const data = response.data.poles || [];
      setPoles(data);
      dataCache.set(cacheKey, data);
    } catch (err) {
      toast.error('Failed to load poles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPoles();
  }, [viewMode]); // Re-fetch when view mode changes

  // Get unique values for filters
  const uniqueLengths = useMemo(() => [...new Set(poles.map(p => p.length).filter(Boolean))].sort((a, b) => {
    const parseL = (l) => { const m = l?.match(/(\d+)'(\d+)?/); return m ? parseInt(m[1]) * 12 + (parseInt(m[2]) || 0) : 0; };
    return parseL(a) - parseL(b);
  }), [poles]);

  const uniqueWeights = useMemo(() => [...new Set(poles.map(p => p.weight).filter(Boolean))].sort((a, b) => parseInt(a) - parseInt(b)), [poles]);

  const uniqueStatuses = useMemo(() => [...new Set(poles.map(p => p.status).filter(Boolean))], [poles]);

  // Filter poles
  const filteredPoles = useMemo(() => {
    return poles.filter(pole => {
      if (statusFilter !== 'all' && pole.status !== statusFilter) return false;
      if (lengthFilter !== 'all' && pole.length !== lengthFilter) return false;
      if (weightFilter !== 'all' && pole.weight !== weightFilter) return false;
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return [pole.pole_code, pole.serial_num, pole.length, pole.weight, pole.brand, pole.tag_id?.toString(), pole.renter_name]
          .some(field => (field || '').toLowerCase().includes(search));
      }
      return true;
    });
  }, [poles, searchTerm, statusFilter, lengthFilter, weightFilter]);

  const openCreateDialog = () => {
    setEditingPole(null);
    setFormData({
      pole_code: '',
      serial_num: '',
      length: '',
      weight: '',
      brand: 'ESSX',
      status: 'in stock',
      tag_id: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (pole) => {
    setEditingPole(pole);
    setFormData({
      pole_code: pole.pole_code || '',
      serial_num: pole.serial_num || '',
      length: pole.length || '',
      weight: pole.weight || '',
      brand: pole.brand || 'ESSX',
      status: pole.status || 'in stock',
      tag_id: pole.tag_id?.toString() || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.length || !formData.weight) {
      toast.error('Length and weight are required');
      return;
    }

    setSaving(true);
    try {
      if (editingPole) {
        // Update existing pole
        await axios.put(`${API}/poles/${editingPole.id}`, formData);
        toast.success('Pole updated successfully');
      } else {
        // Create new pole
        await axios.post(`${API}/poles`, formData);
        toast.success('Pole created successfully');
      }
      setDialogOpen(false);
      fetchPoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save pole');
    } finally {
      setSaving(false);
    }
  };

  const handleViewRental = (rentalId, e) => {
    if (e.metaKey || e.ctrlKey) {
      window.open(`/rentals/${rentalId}`, '_blank');
    } else {
      navigate(`/rentals/${rentalId}`);
    }
  };

  const handleViewUser = (userId, e) => {
    if (e.metaKey || e.ctrlKey) {
      window.open(`/users/${userId}`, '_blank');
    } else if (onUserClick) {
      onUserClick(userId);
    }
  };

  const exportCSV = () => {
    const headers = ['Pole Code', 'Length', 'Weight', 'Brand', 'Serial Number', 'Tag ID', 'Status', 'Renter', 'Rental ID'];
    const rows = filteredPoles.map(p => [
      p.pole_code || '',
      p.length || '',
      p.weight || '',
      p.brand || '',
      p.serial_num || '',
      p.tag_id || '',
      p.status || '',
      p.renter_name || '',
      p.rental_id || ''
    ]);
    const csvContent = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `poles_inventory_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredPoles.length} poles to CSV`);
  };

  const openPoleHistory = async (pole) => {
    setHistoryPole(pole);
    setHistoryDialogOpen(true);
    setLoadingHistory(true);
    try {
      const poleId = pole.pole_id || pole.id;
      const res = await axios.get(`${API}/poles/${poleId}/history`);
      setHistoryData(res.data);
    } catch (err) {
      toast.error("Failed to load pole history");
    } finally {
      setLoadingHistory(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {/* Header - Mobile responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-slate-900">Poles Inventory</h2>
          <p className="text-xs sm:text-sm text-slate-500">{filteredPoles.length} of {poles.length} poles</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-slate-50">
            <button
              onClick={() => setViewMode('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'all' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="view-all-poles"
            >
              All Poles
            </button>
            <button
              onClick={() => setViewMode('available')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'available' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="view-available-poles"
            >
              Available
            </button>
          </div>
          <Button variant="outline" onClick={exportCSV} className="hidden sm:flex" data-testid="export-poles-csv">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={openCreateDialog} data-testid="create-pole-btn" className="hidden sm:flex">
            <Plus className="w-4 h-4 mr-2" />
            Add Pole
          </Button>
        </div>
      </div>

      {/* Floating Action Button for Mobile */}
      <Button 
        onClick={openCreateDialog} 
        className="sm:hidden fixed bottom-20 right-4 z-50 w-14 h-14 rounded-full shadow-lg bg-slate-900 hover:bg-slate-800"
        data-testid="fab-add-pole"
      >
        <Plus className="w-6 h-6" />
      </Button>

      {/* Filters - Scrollable on mobile */}
      <div className="flex items-center gap-2 sm:gap-4 flex-nowrap overflow-x-auto pb-2 bg-white p-3 sm:p-4 rounded-lg border border-slate-200 scrollbar-hide">
        <div className="relative flex-shrink-0 w-[160px] sm:w-[200px] sm:flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input 
            placeholder="Search..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            className="pl-9 h-9 text-sm"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[120px] sm:w-[150px] h-9 flex-shrink-0">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {uniqueStatuses.map(status => (
              <SelectItem key={status} value={status}>{status}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={lengthFilter} onValueChange={setLengthFilter}>
          <SelectTrigger className="w-[100px] sm:w-[130px] h-9 flex-shrink-0">
            <SelectValue placeholder="Length" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Lengths</SelectItem>
            {uniqueLengths.map(len => (
              <SelectItem key={len} value={len}>{len}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={weightFilter} onValueChange={setWeightFilter}>
          <SelectTrigger className="w-[100px] sm:w-[130px] h-9 flex-shrink-0">
            <SelectValue placeholder="Weight" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Weights</SelectItem>
            {uniqueWeights.map(wt => (
              <SelectItem key={wt} value={wt}>{wt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchPoles} className="h-9 w-9 p-0 flex-shrink-0">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Poles Table - Scrollable */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <ScrollArea className="h-[calc(100vh-320px)]">
          <div className="overflow-x-auto">
            <Table className="min-w-[800px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">Pole Code</TableHead>
                  <TableHead className="whitespace-nowrap">Serial #</TableHead>
                  <TableHead className="whitespace-nowrap">Length</TableHead>
                  <TableHead className="whitespace-nowrap">Weight</TableHead>
                  <TableHead className="whitespace-nowrap">Brand</TableHead>
                  <TableHead className="whitespace-nowrap">Status</TableHead>
                  <TableHead className="whitespace-nowrap">Current Rental</TableHead>
                <TableHead>Renter</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPoles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-slate-500 py-8">
                    {searchTerm || statusFilter !== 'all' || lengthFilter !== 'all' || weightFilter !== 'all' 
                      ? 'No poles matching filters' 
                      : 'No poles found'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredPoles.map((pole) => (
                  <TableRow key={pole.id} className="hover:bg-slate-50">
                    <TableCell className="font-mono font-medium">
                      {pole.pole_code || '-'}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {pole.serial_num || '-'}
                    </TableCell>
                    <TableCell className="font-mono">
                      {pole.length || '-'}
                    </TableCell>
                    <TableCell className="font-mono">
                      {pole.weight || '-'}
                    </TableCell>
                    <TableCell>
                      {pole.brand || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={
                        pole.status === 'in stock' ? 'success' :
                        pole.status === 'rented' ? 'default' :
                        pole.status === 'ordered' ? 'secondary' :
                        pole.status === 'maintenance' ? 'warning' :
                        'outline'
                      }>
                        {pole.status || 'unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {pole.rental_id ? (
                        <span 
                          className="text-blue-600 hover:underline cursor-pointer font-mono text-sm"
                          onClick={(e) => handleViewRental(pole.rental_id, e)}
                        >
                          #{pole.rental_id}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {pole.renter_name ? (
                        <span 
                          className="text-blue-600 hover:underline cursor-pointer text-sm"
                          onClick={(e) => pole.user_id && handleViewUser(pole.user_id, e)}
                        >
                          {pole.renter_name}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openPoleHistory(pole)}
                          className="h-8 w-8 p-0 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                          data-testid={`history-pole-${pole.id}`}
                        >
                          <History className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openEditDialog(pole)}
                          className="h-8 w-8 p-0"
                          data-testid={`edit-pole-${pole.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </ScrollArea>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md overflow-visible">
          <DialogHeader>
            <DialogTitle>
              {editingPole ? 'Edit Pole' : 'Add New Pole'}
            </DialogTitle>
            <DialogDescription>
              {editingPole ? 'Update pole information' : 'Enter details for the new pole'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pole Code</Label>
                <Input
                  value={formData.pole_code}
                  onChange={(e) => setFormData(prev => ({ ...prev, pole_code: e.target.value }))}
                  placeholder="e.g., 18807"
                />
              </div>
              <div className="space-y-2">
                <Label>Serial Number</Label>
                <Input
                  value={formData.serial_num}
                  onChange={(e) => setFormData(prev => ({ ...prev, serial_num: e.target.value }))}
                  placeholder="e.g., 60769"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Length *</Label>
                <Select 
                  value={formData.length} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, length: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select length" />
                  </SelectTrigger>
                  <SelectContent>
                    {["9'0", "10'0", "10'6", "11'0", "11'6", "12'1", "12'7", "13'1", "13'7", "14'1", "14'7", "15'1", "15'7", "16'1", "16'5", "16'9"].map(len => (
                      <SelectItem key={len} value={len}>{len}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Weight *</Label>
                <Input
                  value={formData.weight}
                  onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                  placeholder="e.g., 150"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Brand</Label>
                <Select 
                  value={formData.brand} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, brand: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ESSX">ESSX</SelectItem>
                    <SelectItem value="UCS">UCS</SelectItem>
                    <SelectItem value="Pacer">Pacer</SelectItem>
                    <SelectItem value="Gill">Gill</SelectItem>
                    <SelectItem value="Nordic">Nordic</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="in stock">In Stock</SelectItem>
                    <SelectItem value="rented">Rented</SelectItem>
                    <SelectItem value="ordered">Ordered</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="broken">Broken</SelectItem>
                    <SelectItem value="retired">Retired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tag ID</Label>
              <Input
                value={formData.tag_id}
                onChange={(e) => setFormData(prev => ({ ...prev, tag_id: e.target.value }))}
                placeholder="RFID or barcode tag ID"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingPole ? 'Save Changes' : 'Create Pole'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pole History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={(open) => { setHistoryDialogOpen(open); if (!open) { setHistoryData(null); setHistoryPole(null); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              Pole History — {historyPole?.pole_code || `#${historyPole?.id}`}
            </DialogTitle>
            <DialogDescription>
              {historyPole?.length} - {historyPole?.weight} | {historyPole?.brand || 'Unknown brand'}
              {historyPole?.serial_num ? ` | SN: ${historyPole.serial_num}` : ''}
            </DialogDescription>
          </DialogHeader>

          {loadingHistory ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : historyData ? (
            <div className="space-y-5">
              {/* Rental Associations */}
              {historyData.rentals?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Rentals</h4>
                  <div className="space-y-2">
                    {historyData.rentals.map(r => (
                      <div 
                        key={r.rental_id} 
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => navigate(`/rentals/${r.rental_id}`)}
                      >
                        <div>
                          <span className="font-mono text-sm font-semibold text-blue-600">Rental #{r.rental_id}</span>
                          <span className="text-sm text-slate-600 ml-2">{r.renter_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={
                            r.rental_status === 'Active' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                            r.rental_status === 'Completed' ? 'bg-slate-100 text-slate-600' :
                            'bg-amber-100 text-amber-700 border-amber-200'
                          }>
                            {r.rental_status || 'Unknown'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pickup/Return Timeline */}
              {historyData.pickup_returns?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">
                    Checkout / Return Activity ({historyData.pickup_returns.length})
                  </h4>
                  <div className="space-y-2">
                    {historyData.pickup_returns.map(pr => (
                      <div 
                        key={pr.id}
                        className={`p-3 rounded-lg border ${
                          pr.pole_role === 'swapped_in' ? 'border-emerald-200 bg-emerald-50/40' :
                          pr.pole_role === 'swapped_out' ? 'border-red-200 bg-red-50/40' :
                          pr.pole_role === 'checked_out' ? 'border-emerald-200 bg-emerald-50/40' :
                          'border-amber-200 bg-amber-50/40'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            {pr.pole_role === 'swapped_in' ? (
                              <div className="flex items-center gap-1 px-2 py-0.5 bg-emerald-100 rounded-full">
                                <Repeat className="w-3.5 h-3.5 text-emerald-600" />
                                <span className="text-xs font-semibold text-emerald-700">Swapped In</span>
                              </div>
                            ) : pr.pole_role === 'swapped_out' ? (
                              <div className="flex items-center gap-1 px-2 py-0.5 bg-red-100 rounded-full">
                                <Repeat className="w-3.5 h-3.5 text-red-600" />
                                <span className="text-xs font-semibold text-red-700">Swapped Out</span>
                              </div>
                            ) : pr.pole_role === 'checked_out' ? (
                              <div className="flex items-center gap-1 px-2 py-0.5 bg-emerald-100 rounded-full">
                                <ArrowUpFromLine className="w-3.5 h-3.5 text-emerald-600" />
                                <span className="text-xs font-semibold text-emerald-700">Picked Up</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 rounded-full">
                                <ArrowDownToLine className="w-3.5 h-3.5 text-amber-600" />
                                <span className="text-xs font-semibold text-amber-700">Returned</span>
                              </div>
                            )}
                            <span className="text-sm text-slate-700 font-medium">{pr.user_name}</span>
                          </div>
                          <span className="text-xs text-slate-500">
                            {pr.created_at ? new Date(pr.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                          </span>
                        </div>

                        {/* Show swap details */}
                        {pr.swap && (
                          <div className="flex items-center gap-2 mt-2 text-xs text-slate-600">
                            <span className="font-mono">{pr.pole_length} - {pr.pole_weight}</span>
                            <ArrowRight className="w-3 h-3 text-slate-400" />
                            <span className="font-mono">{pr.swap_pole_length} - {pr.swap_pole_weight}</span>
                          </div>
                        )}

                        {pr.notes && (
                          <div className="mt-1 text-xs text-slate-500 italic">{pr.notes}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pole History (additions/removals) */}
              {historyData.pole_history?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Rental Add/Remove Log</h4>
                  <div className="space-y-1.5">
                    {historyData.pole_history.map(ph => (
                      <div key={ph.id} className="flex items-center justify-between p-2 bg-slate-50 rounded border text-sm">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={ph.action === 'added' ? 'text-emerald-600 border-emerald-200' : 'text-red-600 border-red-200'}>
                            {ph.action === 'added' ? 'Added' : 'Removed'}
                          </Badge>
                          <span className="text-slate-600">
                            {ph.action_type === 'swap' ? 'via Swap' : ph.action_type === 'checkout' ? 'via Checkout' : ph.action_type === 'return' ? 'via Return' : ph.action_type}
                          </span>
                          {ph.renter_name && <span className="text-slate-500">• {ph.renter_name}</span>}
                          {ph.rental_id && (
                            <span className="text-blue-600 cursor-pointer hover:underline font-mono text-xs" onClick={() => navigate(`/rentals/${ph.rental_id}`)}>
                              R#{ph.rental_id}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-slate-400">
                          {ph.created_at ? new Date(ph.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!historyData.rentals?.length && !historyData.pickup_returns?.length && !historyData.pole_history?.length) && (
                <div className="text-center py-8 text-slate-500">
                  <History className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>No history found for this pole</p>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Check-ins/Check-outs Tab

export default PolesTab;
